import Vue from "vue";

// The Tracking filter panel is defined by the backend (/notifications/log/filters/):
// which filters exist, their type, labels and options. This module keeps those
// definitions and the values picked in them. A filter's type says its empty value.
const EMPTY_VALUES = {
  date_range: () => [],
  multi: () => [],
  select: () => null,
  text: () => "",
};

export function emptyFilterValue(def) {
  return (EMPTY_VALUES[def.type] || EMPTY_VALUES.text)();
}

// DateRangeFilter keeps the picked range's display text beside the range.
export function displayKey(def) {
  return `${def.key}_display`;
}

export function hasFilterValue(def, value) {
  if (def.type === "date_range") return Array.isArray(value) && value.length > 1;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  return value !== null && value !== undefined;
}

// What a chip says after its prefix: the picked options' labels, or the text.
export function filterValueText(def, filters) {
  if (def.type === "date_range") return filters[displayKey(def)];
  const value = filters[def.key];
  const values = Array.isArray(value) ? value : [value];
  if (!def.options) return values.join(", ");
  return values
    .map((v) => (def.options.find((o) => o.value === v) || { label: v }).label)
    .join(", ");
}

// A request's params for every filter in `defs` that holds a value.
export function filterRequestParams(defs, filters) {
  const params = {};
  defs.forEach((def) => {
    const value = filters[def.key];
    if (!hasFilterValue(def, value)) return;
    if (def.type === "date_range") {
      const [from, to] = [...value].sort();
      params[def.params.from] = from;
      params[def.params.to] = to;
    } else if (Array.isArray(value)) {
      params[def.key] = value.join(",");
    } else {
      params[def.key] = typeof value === "string" ? value.trim() : value;
    }
  });
  return params;
}

// One chip per filter that holds a value, prefixed with its chip text, in the
// shape filterChipsMixin reads.
export function filterChipConfig(defs) {
  return defs.map((def) => {
    const clear = { [def.key]: emptyFilterValue(def) };
    if (def.type === "date_range") clear[displayKey(def)] = "";
    return {
      key: def.key,
      isActive: (f) => hasFilterValue(def, f[def.key]),
      label: (f) => `${def.chip}: ${filterValueText(def, f)}`,
      clear,
    };
  });
}

const baseFilters = () => ({
  page: 1,
  // Status tab: "" = all, or one of the statuses the list's tabs carry.
  status: "",
  selected_tab: 0,
});

function defaultsFor(defs) {
  const filters = baseFilters();
  defs.forEach((def) => {
    filters[def.key] = emptyFilterValue(def);
    if (def.type === "date_range") filters[displayKey(def)] = "";
  });
  return filters;
}

export default {
  namespaced: true,
  state: () => ({
    defs: [],
    filters: baseFilters(),
  }),

  mutations: {
    // A filter already picked keeps its value when the definitions reload.
    SET_DEFS(state, defs) {
      const defaults = defaultsFor(defs);
      Object.keys(defaults).forEach((key) => {
        if (!(key in state.filters)) Vue.set(state.filters, key, defaults[key]);
      });
      state.defs = defs;
    },
    RESET_FILTERS(state) {
      state.filters = defaultsFor(state.defs);
    },
    // Signed out: the next teammate, maybe another lender, starts clean -- no
    // filters, and no teammate options from this lender.
    CLEAR(state) {
      state.defs = [];
      state.filters = baseFilters();
    },
  },
  actions: {
    setDefs({ commit }, defs) {
      commit("SET_DEFS", defs);
    },
    resetFilters({ commit }) {
      commit("RESET_FILTERS");
    },
    clear({ commit }) {
      commit("CLEAR");
    },
  },
  getters: {
    // Fresh copy of the filter defaults; used by filterChipsMixin's
    // dev coverage guard to detect applied-but-unregistered filters.
    getInitialFilters: (state) => defaultsFor(state.defs),
    getFilters: (state) => state.filters,
    getDefs: (state) => state.defs,
  },
};
